import React, { useState } from 'react';
import { Divider, Empty, Space, Spin, Timeline, Tooltip, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faRedo, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import ChangeDetails from './ChangeDetails';
import { useGetChangeLogs } from '../../hooks/useChangeLogs';
import { ErrorMessage } from '@shared/components/ErrorMessage';
import { formatDateForDisplay, DISPLAY_DATE_VARIANTS } from '@shared/utils/dateUtils';
import { Filters } from './Filters';
import { UserTooltip } from './UserTooltip';
import { TimelineSkeleton } from './TimelineSkeleton';

const { Text } = Typography;

const style = { minHeight: '4em' };

const actionTypeConfig = {
  'create': { title: 'Creación', color: 'green', dot: <FontAwesomeIcon icon={faSave} /> },
  'update': { title: 'Actualización', color: 'blue', dot: <FontAwesomeIcon icon={faEdit} /> },
  'delete': { title: 'Eliminación', color: 'red', dot: <FontAwesomeIcon icon={faTrash} /> },
  'requote': { title: 'Recotización', color: 'orange', dot: <FontAwesomeIcon icon={faRedo} /> },
};

export const ChangeLogs = ({ entity, entityId }) => {
  const [filters, setFilters] = useState({});
  const uniqueUsersMap = new Map();

  // isLoading solo se ejecuta en la primera consulta, isFetching se ejecuta en todas las consultas
  const { data, isLoading, isFetching, isError, error } = useGetChangeLogs({ entity, entityId, ...filters });

  const items = data?.map((log) => {
    uniqueUsersMap.set(log.user.id, log.user);

    const actionInfo = actionTypeConfig[log.action.type] || {};
    const { color, dot } = actionInfo;
    let title = actionInfo.title;

    if (['update', 'requote'].includes(log.action.type)) {
      const tooltipMap = {
        'update': 'campos',
        'requote': 'aseguradoras',
      }
      const count = Object.keys(log.action.changes).length

      title = (
        <Space size='small'>
          {title}
          <Tooltip title={`${count} ${tooltipMap[log.action.type]}`} >
            <Text type="secondary">({count})</Text>
          </Tooltip>
        </Space>
      );
    }

    return {
      color, dot, style,
      label: (
        <Space direction='vertical' size={0} style={{ marginInlineEnd: 8 }}>
          <Text strong>
            {formatDateForDisplay(log.metadata.timestamp, { variant: DISPLAY_DATE_VARIANTS.DATE_TIME })}
          </Text>
          <UserTooltip record={log.user}>
            <Text type='secondary'>{log.user.username}</Text>
          </UserTooltip>
        </Space>
      ),
      children: <ChangeDetails entity={log.entity} actionType={log.action.type} originEntity={log.action.originEntity} title={title} changes={log.action.changes} />,
    };
  });

  const hasItems = items?.length > 0;

  const handleSearch = (values) => {
    setFilters(values);
  }

  if (isError) return <ErrorMessage error={error} />

  return (
    <Spin spinning={isFetching}>
      <Filters onSearch={handleSearch} users={Array.from(uniqueUsersMap.values())} compact />
      <Divider size='middle' />
      {isLoading && !hasItems && <TimelineSkeleton />}
      {!isLoading && !hasItems && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      {hasItems && <Timeline mode='left' items={items} />}
    </Spin>
  );
};
